import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, Transition as _Transition, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "filter-box" }
const _hoisted_2 = { class: "button-row" }

import {
  DashboardDropdownFilterLevel,
  DashboardDropdownFilterBuilding,
  DashboardDropdownFilterComplex,
  DashboardDropdownFilter
} from '../components/filters'
import { ComplexProperties, BuildingProperties, ComplexFeature, LevelProperties } from '../../types'

import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import { ref, watch, computed, nextTick, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { InformationResponse, LocaleString } from '@/api-client/models/utilization/Contracts'
import { FeatureType, useFilters } from '@/composables/useFilters'
import { nullableSet } from '@/utils/set'
import { getLevelsv1 } from '@/services/floors.service'
import { useFetchGet } from '@/composables/useFetch'
import { TicketStatus, ticketStatuses } from '@/api-client/models/tickets/Contracts'

type Props = {
  information: InformationResponse | undefined
  isCollapsed: boolean
}
type Emits = {
  (e: 'resetForm'): void
  (e: 'submitForm'): void
}
type TicketType = {
  id: string
  name: LocaleString
}


export default /*@__PURE__*/_defineComponent({
  __name: 'TicketAnalysisFilters',
  props: {
    information: {},
    isCollapsed: { type: Boolean }
  },
  emits: ["resetForm", "submitForm"],
  setup(__props: any, { emit: __emit }) {

const props = __props
const emits = __emit
const vuex = useStore()

const filters = useFilters(FeatureType.TicketAnalysis)
const { t, setLocaleMessage, getLocaleMessage } = useI18n()

const htmlRef = ref<HTMLElement | null>(null)
const complexes = computed<ComplexProperties[]>(() =>
  vuex.getters.allComplexes.map((x: ComplexFeature) => x.properties)
)
const buildings = computed<BuildingProperties[]>(() =>
  vuex.getters.allBuildings.map((x: ComplexFeature) => x.properties)
)
const allLevels = ref<LevelProperties[]>([])
const filteredBuildings = computed<BuildingProperties[]>(() => {
  if (!filters.current.complex) {
    return buildings.value
  }
  const id = filters.current.complex.belongsToId
  return buildings.value.filter((x) => x.complexId === id)
})

const { data: ticketTypes } = useFetchGet<TicketType[]>('/ticket/v1/types')

const typeOptions = computed<string[]>(() => ticketTypes.value?.map((x) => x.id) ?? [])

const statusOptions = computed<TicketStatus[]>(() => Object.keys(ticketStatuses) as TicketStatus[])

watch(ticketTypes, () => {
  if (ticketTypes.value) {
    for (const type of ticketTypes.value) {
      for (const locale in type.name) {
        const existing = getLocaleMessage(locale)
        setLocaleMessage(locale, {
          ...existing,
          [type.id]: type.name[locale]
        })
      }
    }
  }
  for (const status of statusOptions.value) {
    for (const locale in ticketStatuses[status]) {
      const existing = getLocaleMessage(locale)
      setLocaleMessage(locale, {
        ...existing,
        [status]: ticketStatuses[status][locale]
      })
    }
  }
})

watch(
  () => props.isCollapsed,
  async () => {
    await nextTick()
    htmlRef.value?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
  }
)
watch(
  () => filters.current.buildings,
  (values) => {
    updateLevelSelection(values)
    if (!filters.current.complex && values.length === 1) {
      updateComplexSelection(values)
    }
  }
)

onMounted(async () => {
  if (filters.current.buildings.length === 1) {
    updateLevelSelection(filters.current.buildings)
    if (!filters.current.complex) {
      updateComplexSelection(filters.current.buildings)
    }
  }
})

function resetForm() {
  emits('resetForm')
}

function complexSelected(complex: ComplexProperties | undefined) {
  filters.$patch({
    current: {
      complex: complex,
      buildings: [],
      ticketTypes: [],
      ticketStatuses: []
    }
  })
}

function updateComplexSelection(buildings: BuildingProperties[]) {
  const complex = complexes.value.find((x) => x.belongsToId === buildings[0].complexId)
  filters.$patch({ current: { complex: complex } })
}

function ticketTypeSelectionChanged(ids: string[]): void {
  filters.$patch({
    current: {
      ticketTypes: ids
    }
  })
}

const updateLevelSelection = async (buildings: BuildingProperties[]): Promise<void> => {
  if (buildings.length === 0) {
    filters.$patch({ current: { level: undefined } })
    return
  }
  if (buildings.length > 1) {
    allLevels.value = []
    filters.$patch({ current: { level: undefined } })
    return
  }
  try {
    const result = await getLevelsv1(buildings[0].id)
    allLevels.value = result!.data
  } catch {
    allLevels.value = []
  }
}

return (_ctx: any,_cache: any) => {
  return (!_ctx.isCollapsed)
    ? (_openBlock(), _createBlock(_Transition, {
        key: 0,
        name: "slide"
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", {
              ref_key: "htmlRef",
              ref: htmlRef,
              class: "filter-container"
            }, [
              _createVNode(_unref(DashboardDropdownFilterComplex), {
                complexes: complexes.value,
                selectedComplex: _unref(filters).current.complex,
                optionsWithData: undefined,
                onSelectionChanged: complexSelected
              }, null, 8, ["complexes", "selectedComplex"]),
              _createVNode(_unref(DashboardDropdownFilterBuilding), {
                buildings: filteredBuildings.value,
                selectedBuildings: _unref(filters).current.buildings,
                presentAsMultiSelect: !!_unref(filters).current.complex,
                optionsWithData: undefined,
                onSelectionChanged: _cache[0] || (_cache[0] = (buildings) => _unref(filters).$patch({ current: { buildings } }))
              }, null, 8, ["buildings", "selectedBuildings", "presentAsMultiSelect"]),
              _createVNode(_unref(DashboardDropdownFilterLevel), {
                levels: allLevels.value,
                selectedLevel: _unref(filters).current.level,
                optionsWithData: _unref(nullableSet)(_ctx.information?.optionsWithData?.levels),
                onSelectionChanged: _cache[1] || (_cache[1] = (level) => _unref(filters).$patch({ current: { level } }))
              }, null, 8, ["levels", "selectedLevel", "optionsWithData"]),
              _createVNode(_unref(DashboardDropdownFilter), {
                label: _unref(t)('general__text__types'),
                searchType: _unref(t)('general__text__types').toLowerCase(),
                selections: _unref(filters).current.ticketTypes,
                options: typeOptions.value,
                onSelectionChanged: ticketTypeSelectionChanged
              }, null, 8, ["label", "searchType", "selections", "options"]),
              _createVNode(_unref(DashboardDropdownFilter), {
                label: _unref(t)('general__text__statuses'),
                searchType: _unref(t)('general__text__statuses').toLowerCase(),
                selections: _unref(filters).current.ticketStatuses,
                options: statusOptions.value,
                onSelectionChanged: _cache[2] || (_cache[2] = (ticketStatuses) => _unref(filters).$patch({ current: { ticketStatuses } }))
              }, null, 8, ["label", "searchType", "selections", "options"])
            ], 512),
            _createElementVNode("div", _hoisted_2, [
              _createVNode(ButtonOutline, {
                size: "small",
                buttonText: _unref(t)('general__text__dashboard_reset_selections'),
                onOnClicked: resetForm
              }, null, 8, ["buttonText"]),
              _createVNode(ButtonPrimary, {
                size: "small",
                disabled: _unref(filters).isCurrentFilterSubmitted,
                buttonText: _unref(t)('general__text__dashboard_update_selections'),
                onOnClicked: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('submitForm')))
              }, null, 8, ["disabled", "buttonText"])
            ])
          ])
        ]),
        _: 1
      }))
    : _createCommentVNode("", true)
}
}

})